import { Injectable } from '@angular/core';

import { ContextService } from './context.service';
import { OrganizationModuleKey } from '../models/organization.model';

export interface ImpersonatedOrganization {
  id: string;
  name: string;
}

const STORAGE_KEY = 'impersonatedOrganization';
const MODULES_STORAGE_KEY = 'impersonatedOrganizationModules';

/**
 * "Enter Organization" for the System Admin (AC 7.1–7.5).
 *
 * <p>Entering sets the tenant (the same <code>selectedCompanyId</code> the old header picker used)
 * and remembers which organization is being impersonated, so the shell can show the banner and limit
 * the System Admin to organization-level features until they exit. Both are switched with a full page
 * load so every service starts again with the new context.</p>
 *
 * <p>TODO(backend): the restriction is only enforced in the UI; the API should also scope a System
 * Admin's requests while impersonating.</p>
 */
@Injectable({ providedIn: 'root' })
export class ImpersonationService {
  private current: ImpersonatedOrganization | null = this.read();
  /**
   * Active modules of the entered organization. The System Admin's own /me roles don't include it,
   * so the shell loads them (see setActiveModules); null until known. Kept in storage for reloads.
   */
  private activeModules: OrganizationModuleKey[] | null = this.readModules();

  constructor(private contextSvc: ContextService) {}

  get organization(): ImpersonatedOrganization | null {
    return this.current;
  }

  get isActive(): boolean {
    return !!this.current;
  }

  setActiveModules(modules: OrganizationModuleKey[]): void {
    this.activeModules = modules;
    try {
      localStorage.setItem(MODULES_STORAGE_KEY, JSON.stringify(modules));
    } catch {
      // Loaded again on the next page load.
    }
  }

  /**
   * Asset Management belongs with Repair & Maintenance: inside an organization without an active
   * R&M module it's hidden. Shown while the modules aren't known yet.
   */
  get isAssetManagementAvailable(): boolean {
    if (!this.current || !this.activeModules) return true;
    return this.activeModules.includes(OrganizationModuleKey.RepairAndMaintenance);
  }

  /** Enters the organization and reloads on the homepage. */
  enter(organization: ImpersonatedOrganization): void {
    this.current = { id: organization.id, name: organization.name };
    this.activeModules = null;
    try {
      localStorage.removeItem(MODULES_STORAGE_KEY);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.current));
    } catch {
      // Without storage the banner can't survive the reload; the tenant switch still happens.
    }
    this.contextSvc.setCompanyId(organization.id);
    window.location.href = '/';
  }

  /** AC 7.2: back to the System Admin portal. */
  exit(): void {
    this.clear();
    window.location.href = '/companies/list';
  }

  /** Forgets the impersonation without navigating (e.g. on logout). */
  clear(): void {
    this.current = null;
    this.activeModules = null;
    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(MODULES_STORAGE_KEY);
    } catch {
      // Nothing stored.
    }
  }

  /**
   * AC 7.3, 7.4: while impersonating, only organization-level areas (companies, locations, users,
   * roles, vendors, assets, the homepage and personal pages) can be opened.
   */
  isRouteAllowed(url: string): boolean {
    if (!this.current) return true;
    const path = (url || '').split(/[?#]/)[0].replace(/^\/+/, '').toLowerCase();
    if (!this.isAssetManagementAvailable && (path === 'asset-management' || path.startsWith('asset-management/'))) {
      return false;
    }
    return !BLOCKED_WHILE_IMPERSONATING.some((prefix) => path === prefix || path.startsWith(prefix + '/'));
  }

  /**
   * The System Admin portal (not inside an organization) only has Homepage, Organizations, Users and
   * personal pages; organization-level areas need "Enter Organization" first.
   */
  isPortalRouteAllowed(url: string): boolean {
    const path = (url || '').split(/[?#]/)[0].replace(/^\/+/, '').toLowerCase();
    return !BLOCKED_IN_PORTAL.some((prefix) => path === prefix || path.startsWith(prefix + '/'));
  }

  private readModules(): OrganizationModuleKey[] | null {
    try {
      const parsed = JSON.parse(localStorage.getItem(MODULES_STORAGE_KEY) ?? 'null');
      return Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  private read(): ImpersonatedOrganization | null {
    try {
      const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null');
      return parsed?.id && parsed?.name ? { id: parsed.id, name: parsed.name } : null;
    } catch {
      return null;
    }
  }
}

/** Module and platform routes a System Admin can't reach while inside an organization. */
const BLOCKED_WHILE_IMPERSONATING: readonly string[] = [
  'accounts-payable',
  'expenses',
  'repairs-and-maintenance',
  'approval-processes',
  'approval-process',
  'meal-dynamics-ai',
  'companies',
  'company',
];

/** Organization-level routes a System Admin can't open from the portal, only inside an organization. */
const BLOCKED_IN_PORTAL: readonly string[] = [
  'roles',
  'stores',
  'store',
  'store-groups',
  'store-group',
  'vendors',
  'vendor',
  'asset-management',
  'accounts-payable',
  'expenses',
  'repairs-and-maintenance',
  'approval-processes',
  'approval-process',
  'meal-dynamics-ai',
];
